import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import useBenefitsDrawerFilters, {
  type BenefitsDrawerApplyPayload,
  type BenefitsDrawerFilterDraft,
} from '../../benefits-list/components/benefits-drawer-filters.hook';

type Props = {
  fixedFilter: 'theme' | 'category';
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (payload: BenefitsDrawerApplyPayload) => void;
  appliedFilters: BenefitsDrawerFilterDraft | null;
};

const filterCardSx = {
  borderRadius: 2.5,
  boxShadow: 'none',
  borderColor: 'grey.200',
};

export default function ThemeBenefitsDrawerFilters(props: Props) {
  const { fixedFilter, isOpen, onClose, onApplyFilters, appliedFilters } = props;
  const hook = useBenefitsDrawerFilters({ isOpen, onClose, onApplyFilters, appliedFilters });
  const isThemeFixed = fixedFilter === 'theme';
  const isAllTierSelected = hook.draftFilters.tierSort.includes('all');
  const isCompanyType = hook.draftFilters.localType === 'company';
  const locationFieldOptions = isCompanyType ? hook.companyOptions : hook.locationOptions;
  const locationFieldLabel = isCompanyType ? 'Empresa' : 'Localização';
  const isLocationFieldLoading = isCompanyType ? hook.loadingCompanies : hook.loadingLocations;
  const relatedOptions = isThemeFixed ? hook.categoryOptions : hook.themeOptions;
  const selectedRelatedIds = isThemeFixed
    ? hook.draftFilters.categoriesId
    : hook.draftFilters.themesId;
  const loadingRelatedOptions = isThemeFixed ? hook.loadingCategories : hook.loadingThemes;
  const relatedFilterLabel = isThemeFixed ? 'Categorias' : 'Temas';
  const relatedFilterEmptyText = isThemeFixed
    ? 'Nenhuma categoria encontrada'
    : 'Nenhum tema encontrado';

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 560, md: 620 },
          maxWidth: '100vw',
          backgroundColor: 'grey.50',
        },
      }}
      open={isOpen}
      onClose={onClose}
      anchor="right"
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Filtros de Benefícios
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isThemeFixed
                ? 'Refine os benefícios vinculados a este tema'
                : 'Refine os benefícios vinculados a esta categoria'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          <Stack spacing={2}>
            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Tipo de benefício
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={hook.benefitTypeOptions}
                    value={hook.benefitTypeOptions.filter((option) =>
                      hook.draftFilters.benefitType.includes(option.value)
                    )}
                    onChange={(_, selected) =>
                      hook.handleChangeBenefitType(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Tipo de benefício" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {relatedFilterLabel}
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    loading={loadingRelatedOptions}
                    disabled={loadingRelatedOptions}
                    options={relatedOptions}
                    value={relatedOptions.filter((option) =>
                      selectedRelatedIds.includes(option.value)
                    )}
                    onChange={(_, selected) => {
                      const selectedIds = selected.map((item) => item.value);

                      if (isThemeFixed) {
                        hook.handleChangeCategories(selectedIds);
                        return;
                      }

                      hook.handleChangeThemes(selectedIds);
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    noOptionsText={relatedFilterEmptyText}
                    loadingText={`Carregando ${relatedFilterLabel.toLowerCase()}...`}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label={relatedFilterLabel}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingRelatedOptions ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Status
                  </Typography>
                  <Autocomplete
                    options={hook.statusOptions}
                    value={
                      hook.statusOptions.find(
                        (option) => option.value === hook.draftFilters.status
                      ) || null
                    }
                    onChange={(_, selected) => hook.handleChangeStatus(selected?.value || '')}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Status" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Período
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Controller
                      name="startDate"
                      control={hook.control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de início"
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            hook.handleChangeDate('startDate', value);
                          }}
                          slotProps={{ textField: { fullWidth: true, variant: 'filled' } }}
                        />
                      )}
                    />
                    <Controller
                      name="endDate"
                      control={hook.control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de fim"
                          value={field.value}
                          minDate={hook.draftFilters.startDate || undefined}
                          onChange={(value) => {
                            field.onChange(value);
                            hook.handleChangeDate('endDate', value);
                          }}
                          slotProps={{ textField: { fullWidth: true, variant: 'filled' } }}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Tier
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={hook.tierOptions}
                    value={hook.tierOptions.filter((option) =>
                      hook.draftFilters.tierSort.includes(option.value)
                    )}
                    getOptionDisabled={(option) => isAllTierSelected && option.value !== 'all'}
                    onChange={(_, selected) =>
                      hook.handleChangeTierSort(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Tier" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Ambiente de Exibição
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={hook.exhibitionEnvOptions}
                    value={hook.exhibitionEnvOptions.filter((option) =>
                      hook.draftFilters.exhibitionEnv.includes(option.value)
                    )}
                    onChange={(_, selected) =>
                      hook.handleChangeExhibitionEnv(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Ambiente de Exibição" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Empresa ou local
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Tipo de local
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={hook.draftFilters.localType}
                    onChange={(_, value) => value && hook.handleChangeLocalType(value)}
                  >
                    {hook.localTypeOptions.map((option) => (
                      <ToggleButton key={option.value} value={option.value}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Autocomplete
                    freeSolo={isCompanyType}
                    loading={isLocationFieldLoading}
                    options={locationFieldOptions.map((option) => option.value)}
                    value={hook.draftFilters.company}
                    onChange={(_, value) => hook.handleChangeCompany(value || '')}
                    onInputChange={(_, value) => hook.handleChangeCompany(value)}
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label={locationFieldLabel} />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        <Divider />
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={hook.handleClear}>
            Limpar
          </Button>
          <Button variant="contained" onClick={hook.handleApply}>
            Aplicar filtros
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
